import React, { createContext, useState, useEffect, useCallback, useMemo, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { getAllMyMetadata, setBookmark } from '../services/metadataApi';
import { useAuth } from './AuthContext';
import { useResourceLoading } from './ResourceLoadingContext';
import { applyResourceSyncEvent } from '../services/resourceSyncService';

const SYNC_THROTTLE_MS = 200;

let globalResourceMap = null;
let globalLoadPromise = null;
let globalHasLoaded = false;

export const ResourceStateContext = createContext(null);
export const ResourceDispatchContext = createContext(null);

export const useResourceState = () => {
  const context = useContext(ResourceStateContext);
  if (!context) {
    throw new Error('useResourceState must be used within a ResourceProvider');
  }
  return context;
};

export const useResourceDispatch = () => {
  const context = useContext(ResourceDispatchContext);
  if (!context) {
    throw new Error('useResourceDispatch must be used within a ResourceProvider');
  }
  return context;
};

export const useResources = () => {
  return { ...useResourceState(), ...useResourceDispatch() };
};

export const ResourceProvider = ({ children }) => {
  const [resourceMap, setResourceMap] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bookmarksMetadata, setBookmarksMetadata] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const { isAuthenticated } = useAuth();
  const prevIsAuthenticatedRef = useRef(isAuthenticated);
  const { fetchAllResources, processAllResources } = useResourceLoading();
  const syncQueueRef = useRef([]);
  const isProcessingSyncsRef = useRef(false);
  const syncThrottleTimerRef = useRef(null);

  const arrayToMap = (arr) => {
    const map = {};
    if (Array.isArray(arr)) {
      arr.forEach(item => {
        if (item && item.id) map[item.id] = item;
      });
    }
    return map;
  };

  const loadResources = useCallback(async (forceRefresh = false) => {
    if (!isAuthenticated && !forceRefresh) {
      if (globalHasLoaded) {
        globalResourceMap = null;
        globalHasLoaded = false;
        setResourceMap({});
        setBookmarksMetadata([]);
        setError(null);
      }
      return [];
    }

    if (globalLoadPromise) {
      setIsLoading(true);
      try {
        return await globalLoadPromise;
      } catch (err) {
        setError(err.message || "Failed to load resources while waiting");
        return [];
      } finally {
        setIsLoading(false);
      }
    }

    if (!forceRefresh && globalHasLoaded && globalResourceMap) {
      const transformedCached = processAllResources(globalResourceMap, bookmarksMetadata);
      setResourceMap(arrayToMap(transformedCached));
      return transformedCached;
    }

    setIsLoading(true);
    setError(null);

    globalLoadPromise = Promise.all([
      fetchAllResources(),
      getAllMyMetadata()
    ])
      .then(([apiResourceMap, metadata]) => {
        globalResourceMap = apiResourceMap;
        globalHasLoaded = true;
        setBookmarksMetadata(metadata);
        const transformedResources = processAllResources(apiResourceMap, metadata);
        setResourceMap(arrayToMap(transformedResources));
        setRefreshKey(prevKey => prevKey + 1);
        return transformedResources;
      })
      .catch(err => {
        setError(err.message || "Failed to load resources");
        globalHasLoaded = false;
        globalResourceMap = null;
        setResourceMap({});
        throw err;
      })
      .finally(() => {
        setIsLoading(false);
        globalLoadPromise = null;
      });
    try {
        return await globalLoadPromise;
    } catch(err) {
        return [];
    }
  }, [isAuthenticated, bookmarksMetadata, fetchAllResources, processAllResources]);

  useEffect(() => {
    const justLoggedIn = !prevIsAuthenticatedRef.current && isAuthenticated;
    if (justLoggedIn) {
      loadResources(true);
    } else if (!isAuthenticated && prevIsAuthenticatedRef.current) {
      globalResourceMap = null;
      globalHasLoaded = false;
      setResourceMap({});
      setBookmarksMetadata([]);
      setError(null);
    }
    prevIsAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated, loadResources]);

  useEffect(() => {
    if (isAuthenticated && !globalHasLoaded) {
      loadResources();
    }
  }, []);

  const toggleBookmark = useCallback(async (resourceId, isBookmarked) => {
    setResourceMap(prevMap => {
        if (!prevMap[resourceId]) return prevMap;
        return {
            ...prevMap,
            [resourceId]: { ...prevMap[resourceId], isBookmarked }
        };
    });
    try {
      await setBookmark(resourceId, isBookmarked);
      setBookmarksMetadata(prevMeta => {
          const metaIndex = prevMeta.findIndex(m => m.resource_id === resourceId);
          const newMeta = [...prevMeta];
          if (metaIndex > -1) {
              newMeta[metaIndex] = { ...newMeta[metaIndex], metadata: { ...newMeta[metaIndex].metadata, bookmarked: isBookmarked }};
          } else {
              newMeta.push({ resource_id: resourceId, metadata: { bookmarked: isBookmarked } });
          }
          return newMeta;
      });
    } catch (error) {
      setResourceMap(prevMap => {
        if (!prevMap[resourceId]) return prevMap;
        return {
            ...prevMap,
            [resourceId]: { ...prevMap[resourceId], isBookmarked: !isBookmarked }
        };
      });
      setError(`Failed to update bookmark for resource ${resourceId}. Please try again.`);
    }
  }, []);

  const refreshResources = useCallback(async () => {
    console.log("!!! RefreshResources called via context function !!!");
    globalHasLoaded = false;
    globalResourceMap = null;
    return await loadResources(true);
  }, [loadResources]);

  const resourcesArray = useMemo(() => Object.values(resourceMap), [resourceMap]);

  const stateValue = useMemo(() => {
    const getResourceById = (id) => resourceMap[id];
    const getChildrenOf = (parentId) => {
        const directChildren = resourcesArray.filter(resource => String(resource.parentId) === String(parentId));
        if (directChildren.length > 0) return directChildren;
        const parent = resourceMap[parentId];
        if (parent?.children?.length > 0) {
            return parent.children
                .map(childRef => resourceMap[childRef.id])
                .filter(Boolean);
        }
        return [];
    };
    return {
        resources: resourcesArray,
        resourceMap,
        isLoading,
        error,
        refreshKey,
        getResourceById,
        getResourcesByType: (type) => {
            const types = Array.isArray(type) ? type : [type];
            return resourcesArray.filter(resource => types.includes(resource.type) || types.includes(resource.collection_type));
        },
        getChildrenOf,
        getParentChain: (resource) => {
            const result = [];
            let current = resource;
            const visited = new Set();
            while (current && current.parentId && !visited.has(current.parentId)) {
                visited.add(current.parentId);
                const parent = resourceMap[current.parentId];
                if (parent) {
                    result.unshift(parent);
                    current = parent;
                } else {
                    break;
                }
            }
            return result;
        }
    };
  }, [resourceMap, resourcesArray, isLoading, error, refreshKey]);

  const processSyncQueue = useCallback(async () => {
    if (isProcessingSyncsRef.current) return;
    isProcessingSyncsRef.current = true;
    let currentStateSnapshot = await new Promise(resolve => setResourceMap(current => {
        resolve(current);
        return current;
    }));
    while (syncQueueRef.current.length > 0) {
      const event = syncQueueRef.current.shift();
      try {
        currentStateSnapshot = await applyResourceSyncEvent(currentStateSnapshot, event);
      } catch (e) {
        console.error("[ResourceSyncQueue] Error applying sync event, skipping to next:", e);
      }
    }
    setResourceMap(currentStateSnapshot);
    isProcessingSyncsRef.current = false;
    if (syncQueueRef.current.length > 0) {
        if (!syncThrottleTimerRef.current) {
             syncThrottleTimerRef.current = setTimeout(() => {
                syncThrottleTimerRef.current = null;
                processSyncQueue();
             }, SYNC_THROTTLE_MS);
        }
    }
  }, []);

  const upsertResource = useCallback((resource) => {
    if (!resource || !resource.id) return;
    setResourceMap(prevMap => {
        const type = resource.type || resource.resource_type || prevMap[resource.id]?.type;
        return {
            ...prevMap,
            [resource.id]: {
                ...prevMap[resource.id],
                ...resource,
                type,
                resource_type: type
            }
        };
    });
  }, []);

  const dispatchValue = useMemo(() => {
    const applySyncUpdate = (event) => {
      syncQueueRef.current.push(event);
      if (!isProcessingSyncsRef.current && !syncThrottleTimerRef.current) {
        syncThrottleTimerRef.current = setTimeout(() => {
            syncThrottleTimerRef.current = null;
            processSyncQueue();
        }, SYNC_THROTTLE_MS);
      }
    };
    return {
      refreshResources,
      toggleBookmark,
      applySyncUpdate,
      upsertResource,
    };
  }, [refreshResources, toggleBookmark, processSyncQueue, upsertResource]);

  useEffect(() => {
      return () => {
          if (syncThrottleTimerRef.current) {
              clearTimeout(syncThrottleTimerRef.current);
          }
      };
  }, []);

  return (
    <ResourceStateContext.Provider value={stateValue}>
      <ResourceDispatchContext.Provider value={dispatchValue}>
        {children}
      </ResourceDispatchContext.Provider>
    </ResourceStateContext.Provider>
  );
};

ResourceProvider.propTypes = {
  children: PropTypes.node.isRequired,
};